import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { toast } from "sonner";
import { X } from "lucide-react";
import { adminStatus, adminEmailLog, errText } from "@/lib/api";
import { STATUSES, CONFIRM_STATUSES, fmtDate, statusCls, ghostBtnCls } from "./constants";
import { DataBlock } from "./DataBlock";
import { ReviewPanel } from "./ReviewPanel";
import { InvitationPanel } from "./InvitationPanel";

const EmailLog = ({ entries }) => (
  <div className="mt-6 border border-gold/25 bg-onyx p-4" data-testid="email-log">
    <p className="font-cinzel text-[10px] uppercase tracking-[0.35em] text-gold">Email Log</p>
    {entries.length === 0 && <p className="mt-2 font-body text-xs text-silver/50">No emails logged for this record yet.</p>}
    {entries.map((e) => (
      <div key={e.id} className="mt-2 flex items-center justify-between gap-2 border-b border-silver/10 pb-1.5">
        <span className="truncate font-body text-xs text-silver">{e.subject}</span>
        <span className={"shrink-0 border px-1.5 font-mono text-[10px] font-bold uppercase " + (e.status === "sent" ? "border-[#3ad07a]/60 bg-[#3ad07a]/10 text-[#3ad07a]" : "border-silver/40 text-silver/60")}>{e.status}</span>
      </div>
    ))}
  </div>
);

export const Detail = ({ kind, record, onClose, onUpdated }) => {
  const [emailLog, setEmailLog] = useState([]);

  useEffect(() => {
    adminEmailLog(record.id).then(setEmailLog).catch(() => {});
  }, [record.id]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const changeStatus = async (status) => {
    let extra = {};
    if (status === "interview_requested") {
      const details = window.prompt("Next step details for the applicant (included in their email):", "We'll reach out to schedule a 20-minute conversation this week.");
      if (details === null) return;
      extra.next_step_details = details;
    }
    if (status === "accepted") {
      if (!window.confirm("Accept this applicant? This creates a participant record and emails them their acceptance invitation link.")) return;
      const deadline = window.prompt("Onboarding deadline (optional, e.g. 'June 30'):", "");
      if (deadline) extra.onboarding_deadline = deadline;
    } else if (CONFIRM_STATUSES.includes(status)) {
      if (!window.confirm('Set status to "' + status.replace(/_/g, " ") + '"?')) return;
    }
    try {
      const updated = await adminStatus(kind, record.id, { status, ...extra });
      onUpdated(updated);
      toast.success("Status → " + status.replace(/_/g, " "));
      adminEmailLog(record.id).then(setEmailLog).catch(() => {});
    } catch (err) {
      toast.error(errText(err));
    }
  };

  const title = record.full_name || record.institution_name || record.company_name || record.id;
  const actions = STATUSES[kind].filter((s) => s !== record.status && !["onboarding", "onboarded"].includes(s));

  return createPortal(
    <div className="fixed inset-0 z-[100] flex justify-end bg-onyx/80 backdrop-blur-sm" onClick={onClose} data-testid="record-detail">
      <div className="grain-dark h-full w-full max-w-2xl overflow-y-auto border-l border-gold/40 bg-charcoal p-6 text-cream sm:p-8" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-4">
          <div>
            <p className="font-mono text-xs text-silver/50">{record.id} · {fmtDate(record.created_at)}</p>
            <h2 className="font-display text-3xl uppercase tracking-tight text-metallic">{title}</h2>
            <span className={"mt-1 inline-block border px-2 py-0.5 font-mono text-[10px] font-bold uppercase " + statusCls(record.status)} data-testid="detail-status">
              {record.status.replace(/_/g, " ")}
            </span>
          </div>
          <button onClick={onClose} className={ghostBtnCls + " p-1.5"} data-testid="close-detail-btn" aria-label="Close">
            <X size={18} />
          </button>
        </div>

        <div className="mt-4 flex flex-wrap gap-1.5" data-testid="status-actions">
          {actions.map((s) => (
            <button key={s} onClick={() => changeStatus(s)} data-testid={"status-btn-" + s} className="border border-silver/30 bg-onyx px-2.5 py-1 font-head text-[11px] font-extrabold uppercase tracking-wide text-silver transition-colors hover:border-gold hover:text-goldlight">
              {s.replace(/_/g, " ")}
            </button>
          ))}
        </div>

        {record.participant_invitation && <InvitationPanel record={record} onUpdated={onUpdated} />}

        <div className="mt-6 border border-gold/25 bg-charcoal p-4">
          <p className="mb-3 font-cinzel text-[10px] uppercase tracking-[0.35em] text-gold">Submission</p>
          <DataBlock data={record} />
        </div>

        <ReviewPanel key={record.id} kind={kind} record={record} onSaved={onUpdated} />
        <EmailLog entries={emailLog} />
      </div>
    </div>,
    document.body
  );
};
